import { useCallback, useEffect, useMemo, useState } from 'react';

import { isCarouselOffer, Offer, useCategoryContent, useOffersCarouselCategories } from '@ocx/data-offers';
import { useRudderStack } from '@ocx/data-analytics';
import { useMembership } from '@ocx/data-membership';
import { useConfiguration } from '@ocx/data-configuration';

export type UseOfferCategoriesOptions = {
  persistToStorage?: boolean;
};

export const useOfferCategories = (offers: Offer[], options: UseOfferCategoriesOptions = {}) => {
  const { persistToStorage } = options;
  const { triggerEvent } = useRudderStack();
  const {
    config: { chainId },
  } = useConfiguration();
  const { membership } = useMembership();

  const carouselOffers = useMemo(() => offers.filter(isCarouselOffer), [offers]);
  const membershipRestrictionVerification = membership.fuelAndConvenienceProfile?.restrictionVerification;
  const { categories, refetchCategories, refetchEagerExternalOffers } = useOffersCarouselCategories(
    carouselOffers,
    { chainId, membershipRestrictionVerification },
    { persistToStorage },
  );

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const selectedCategoryBase = useMemo(
    () => categories.find(({ id }) => id === selectedCategoryId) || null,
    [categories, selectedCategoryId],
  );

  const {
    category: selectedCategory,
    isLoading: isCategoryLoading,
    refetch: refetchCategory,
  } = useCategoryContent({
    category: selectedCategoryBase,
    refetchEager: refetchEagerExternalOffers,
  });

  const handleCategoryChanged = useCallback(
    (nextCategoryId: string) => {
      const nextCategory = categories.find(({ id }) => id === nextCategoryId);
      if (!nextCategory || nextCategoryId === selectedCategoryId) {
        return;
      }

      triggerEvent('home_categories', {
        displayCategoryId: nextCategoryId,
        displayCategoryTitle: nextCategory.title,
      });
      setSelectedCategoryId(nextCategoryId);
    },
    [categories, selectedCategoryId, triggerEvent],
  );

  useEffect(() => {
    if (selectedCategory || !categories[0]) {
      return;
    }
    handleCategoryChanged(categories[0].id);
  }, [selectedCategory, handleCategoryChanged, categories]);

  return useMemo(() => {
    return {
      categories,
      selectedCategory,
      onCategoryChange: handleCategoryChanged,
      refetchCategories,
      refetchCategory,
      isCategoryLoading,
    };
  }, [categories, handleCategoryChanged, selectedCategory, refetchCategories, refetchCategory, isCategoryLoading]);
};
